import { useState, useEffect, Component } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Registros from './pages/Registros';
import CDT from './pages/CDT';
import ClientesFijos from './pages/ClientesFijos';
import Gastos from './pages/Gastos';
import IVA from './pages/IVA';
import Rentabilidad from './pages/Rentabilidad';
import Cartera from './pages/Cartera';
import Clientes from './pages/Clientes';
import Academia from './pages/Academia';
import CreditoAutomovil from './pages/CreditoAutomovil';
import TradingInversiones from './pages/TradingInversiones';
import Inversiones from './pages/Inversiones';
import TecnicoPage from './pages/TecnicoPage';
import AdministrativaPage, { SegSocialView } from './pages/AdministrativaPage';
import { NominaPage } from './pages/Gastos';
import Configuracion from './pages/Configuracion';
import RecursosHumanosPage from './pages/RecursosHumanosPage';
import ThemeToggle from './components/ThemeToggle';
import { applyTheme, clearStoragePreservingTheme, getStoredTheme, saveTheme } from './theme';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) return (
      <div style={{ minHeight:'100vh', background:'#08293f', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'rgba(220,38,38,0.1)', border:'1px solid rgba(220,38,38,0.3)', borderRadius:4, padding:24, maxWidth:600, width:'100%' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#dc2626', marginBottom:12, textTransform:'uppercase', letterSpacing:'0.08em' }}>Error en la aplicación</div>
          <pre style={{ fontSize:11, color:'#fca5a5', whiteSpace:'pre-wrap', wordBreak:'break-all', margin:0 }}>{String(this.state.err)}</pre>
          <button onClick={() => { this.setState({err:null}); window.location.reload(); }} style={{ marginTop:16, padding:'8px 16px', background:'#dc2626', color:'#fff', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600 }}>Recargar</button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

const PAGES = {
  dashboard: Dashboard,
  registros: Registros,
  inversiones: Inversiones,
  cdt: Inversiones,
  fijos: ClientesFijos,
  gastos: Gastos,
  iva: IVA,
  rentabilidad: Rentabilidad,
  cartera: Cartera,
  clientes: Clientes,
  academia: Academia,
  creditoAuto: CreditoAutomovil,
  trading: Inversiones,
  configuracion: Configuracion,
};

const MODULE_GROUPS = [
  {
    key: 'dashboard', label: 'Dashboard', code: 'DB',
    items: [{ key: 'dashboard', label: 'Dashboard', description: 'Indicadores principales' }],
  },
  {
    key: 'clientes', label: 'Clientes y finanzas', code: 'CL',
    items: [
      { key: 'clientes', label: 'Clientes', description: 'Fichas, cotizaciones e inventarios' },
      { key: 'registros', label: 'Registros', description: 'Ingresos, servicios y cartera' },
      { key: 'cartera', label: 'Cartera', description: 'Por cobrar y días de vencimiento' },
      { key: 'iva', label: 'IVA', description: 'Generado y descontable' },
      { key: 'gastos', label: 'Gastos', description: 'Fijos y variables' },
      { key: 'rentabilidad', label: 'Rentabilidad', description: 'Clientes, servicios y márgenes' },
    ],
  },
  {
    key: 'empresas', label: 'Empresas y nómina', code: 'EM',
    items: [
      { key: 'tecnicoEmpresas', label: 'Empresas', description: 'Inventario y equipos' },
      { key: 'nomina', label: 'Nómina', description: 'Liquidación, comprobantes e historial' },
      { key: 'ss', label: 'Seguridad social', description: 'Liquidación y pagos PILA' },
    ],
  },
  {
    key: 'personal', label: 'Inversiones y academia', code: 'IN',
    items: [
      { key: 'creditoAuto', label: 'Crédito vehículo', description: 'Cuotas, abonos y saldo' },
      { key: 'academia', label: 'Academia', description: 'Alumnos y pagos' },
      { key: 'inversiones', label: 'Inversiones', description: 'Trading, CDT y Bitcoin' },
    ],
  },
  {
    key: 'administracion', label: 'Administración', code: 'AD', adminOnly: true,
    items: [{ key: 'configuracion', label: 'Configuración', description: 'Usuarios, permisos y respaldos' }],
  },
];

const MODULE_ITEMS = MODULE_GROUPS.flatMap(group => group.items);

const INITIAL_THEME = applyTheme(getStoredTheme());

export default function App() {
  const [user, setUser]         = useState(null);
  const [page, setPage]         = useState('dashboard');
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const [openModuleGroup, setOpenModuleGroup] = useState(null);
  const [adminMode, setAdminMode] = useState(null);
  const [theme, setTheme] = useState(INITIAL_THEME);

  useEffect(() => {
    const token  = localStorage.getItem('token');
    const nombre = localStorage.getItem('nombre');
    const rol    = localStorage.getItem('rol');
    if (token && nombre) setUser({ token, nombre, rol });
  }, []);

  useEffect(() => { saveTheme(theme); }, [theme]);

  const handleLogin = (u) => { setAdminMode(null); setUser(u); };
  const handleLogout = () => { clearStoragePreservingTheme(); setUser(null); };
  const toggleTheme = () => setTheme(current => current === 'dark' ? 'light' : 'dark');

  if (!user) return <ErrorBoundary><Login onLogin={handleLogin} /></ErrorBoundary>;

  // Técnico: página propia sin nav regular
  if (user.rol === 'tecnico') {
    return <div className="app-theme-shell"><TecnicoPage user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} /></div>;
  }

  if (user.rol === 'recursos_humanos') {
    return <div className="app-theme-shell"><ErrorBoundary><RecursosHumanosPage user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} /></ErrorBoundary></div>;
  }

  // Admin/Administrativa: panel propio a menos que haya entrado en modo normal
  if ((user.rol === 'admin' || user.rol === 'administrativa') && adminMode !== 'normal') {
    return (
      <div className="app-theme-shell">
        <ErrorBoundary>
          <AdministrativaPage
            user={user}
            onLogout={handleLogout}
            theme={theme}
            onToggleTheme={toggleTheme}
            onIngresarNormal={(targetPage) => { if (targetPage) setPage(targetPage); setAdminMode('normal'); }}
          />
        </ErrorBoundary>
      </div>
    );
  }

  const Page = PAGES[page] || Dashboard;
  const visibleModuleGroups = MODULE_GROUPS.filter(group => !group.adminOnly || user.rol === 'admin');
  function navTo(key) {
    const item = MODULE_ITEMS.find(module => module.key === key);
    if (item?.disabled) return;
    setPage(key);
    setModuleMenuOpen(false);
    setOpenModuleGroup(null);
  }

  return (
    <ErrorBoundary>
    <div className="app-theme-shell" style={{ minHeight: '100vh', background: 'var(--app-bg)' }}>
      <nav className="app-topbar">
        <div className="app-topbar-inner">
          <div className="app-brand" aria-label="AGE Informática">
            <span className="app-brand-mark">{'//'}</span>
            <strong>AGE</strong>
            <span className="app-brand-name">INFORMÁTICA</span>
          </div>

          <div className="topbar-groups" aria-label="Áreas del sistema">
            {visibleModuleGroups.map(group => {
              const isCurrent = group.items.some(item => item.key === page);
              const isOpen = openModuleGroup === group.key;
              const isSingle = group.items.length === 1;
              if (isSingle) {
                return (
                  <div className="topbar-group" key={group.key}>
                    <button
                      type="button"
                      className={`topbar-group-trigger${isCurrent ? ' is-current' : ''}`}
                      onClick={() => navTo(group.items[0].key)}
                    >
                      <span>{group.label}</span>
                    </button>
                  </div>
                );
              }
              return (
                <div className="topbar-group" key={group.key}>
                  <button
                    type="button"
                    className={`topbar-group-trigger${isCurrent ? ' is-current' : ''}${isOpen ? ' is-open' : ''}`}
                    aria-expanded={isOpen}
                    aria-controls={`group-menu-${group.key}`}
                    onClick={() => {
                      setModuleMenuOpen(false);
                      setOpenModuleGroup(current => current === group.key ? null : group.key);
                    }}
                  >
                    <span>{group.label}</span><b aria-hidden="true">{isOpen ? '▲' : '▼'}</b>
                  </button>
                  {isOpen && (
                    <div className="topbar-group-menu" id={`group-menu-${group.key}`}>
                      <div className="topbar-group-menu-title"><span>{group.code}</span><strong>{group.label}</strong></div>
                      <div className="topbar-group-menu-items">
                        {group.items.map(item => (
                          <button
                            type="button"
                            key={item.key}
                            disabled={item.disabled}
                            className={page === item.key ? 'is-active' : ''}
                            onClick={() => navTo(item.key)}
                          >
                            <span><strong>{item.label}</strong><small>{item.description}</small></span>
                            {item.disabled ? <em>Próximamente</em> : <b aria-hidden="true">›</b>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={`module-launcher mobile-module-launcher${moduleMenuOpen ? ' is-open' : ''}`}
            aria-expanded={moduleMenuOpen}
            aria-controls="mobile-module-menu"
            onClick={() => { setOpenModuleGroup(null); setModuleMenuOpen(open => !open); }}
          >
            <span className="module-launcher-grid" aria-hidden="true"><i /><i /><i /><i /></span>
            <span>Módulos</span>
            <span aria-hidden="true">{moduleMenuOpen ? '▲' : '▼'}</span>
          </button>

          <div className="app-topbar-actions">
            <ThemeToggle theme={theme} onToggle={toggleTheme} variant="nav" />
            {(user.rol === 'admin' || user.rol === 'administrativa') && (
              <button type="button" className="topbar-panel-button" onClick={() => setAdminMode(null)}><span className="panel-label-long">Panel principal</span><span className="panel-label-short">Panel</span></button>
            )}
            <span className="topbar-user" title={user.nombre}>{user.nombre}</span>
            <button type="button" className="topbar-logout" onClick={handleLogout}>Salir</button>
          </div>
        </div>

        {moduleMenuOpen && (
          <div className="module-menu-shell mobile-module-menu-shell" id="mobile-module-menu">
            <div className="module-menu-heading">
              <div><strong>Áreas y módulos</strong><span>Selecciona una opción para continuar</span></div>
              <button type="button" onClick={() => setModuleMenuOpen(false)} aria-label="Cerrar menú">X</button>
            </div>
            <div className="module-menu-groups">
              {visibleModuleGroups.map(group => (
                <section className="module-group" key={group.key} aria-label={group.label}>
                  <div className="module-group-title"><span>{group.code}</span><strong>{group.label}</strong></div>
                  <div className="module-group-items">
                    {group.items.map(item => (
                      <button
                        type="button"
                        key={item.key}
                        disabled={item.disabled}
                        className={page === item.key ? 'is-active' : ''}
                        onClick={() => navTo(item.key)}
                      >
                        <span><strong>{item.label}</strong><small>{item.description}</small></span>
                        {item.disabled ? <em>Próximamente</em> : <b aria-hidden="true">›</b>}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Contenido */}
      <main style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
        {page === 'tecnico' || page === 'tecnicoEmpresas'
          ? <TecnicoPage user={user} onLogout={handleLogout} noHeader initialTab={page === 'tecnicoEmpresas' ? 'empresas' : undefined} />
          : page === 'personal'
            ? <RecursosHumanosPage user={user} onLogout={handleLogout} embedded />
            : page === 'ss'
              ? <SegSocialView onBack={() => navTo('dashboard')} embedded />
              : page === 'nomina'
                ? <NominaPage />
                : <Page />}
      </main>

      <style>{`
        .app-topbar { position: sticky; top: 0; z-index: 80; background: #08293f; border-bottom: 1px solid rgba(0,184,227,0.25); box-shadow: 0 6px 20px rgba(1,15,23,0.14); }
        .app-topbar-inner { width: 100%; height: 64px; margin: 0 auto; padding: 0 22px; display: flex; align-items: center; gap: 16px; }
        .app-brand { display: flex; align-items: baseline; gap: 5px; flex: 0 0 auto; white-space: nowrap; }
        .app-brand-mark { color: #00b8e3; font-size: 19px; font-weight: 800; letter-spacing: -3px; }
        .app-brand strong { color: #00b8e3; font-size: 15px; font-weight: 800; letter-spacing: 3px; }
        .app-brand-name { color: #9fb4c1; font-size: 9px; letter-spacing: 3px; }
        .topbar-groups { min-width: 0; height: 100%; display: flex; align-items: stretch; justify-content: stretch; gap: 4px; flex: 1 1 auto; }
        .topbar-group { position: relative; display: flex; align-items: stretch; flex: 1 1 auto; }
        .topbar-group-trigger {
          position: relative; width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: 0 14px;
          border: 0; background: transparent; color: #a9c0cc; font: 700 12px 'Space Grotesk', sans-serif;
          letter-spacing: 0.02em; cursor: pointer; white-space: nowrap;
        }
        .topbar-group-trigger::after { content: ''; position: absolute; left: 9px; right: 9px; bottom: 0; height: 2px; background: transparent; }
        .topbar-group-trigger:hover, .topbar-group-trigger.is-current, .topbar-group-trigger.is-open { color: #00b8e3; background: rgba(0,184,227,0.07); }
        .topbar-group-trigger.is-current::after, .topbar-group-trigger.is-open::after { background: #00b8e3; }
        .topbar-group-trigger b { color: currentColor; font-size: 7px; font-weight: 500; }
        .topbar-group-menu {
          position: absolute; top: calc(100% + 1px); left: 0; width: 310px; max-height: calc(100vh - 88px); overflow-y: auto;
          padding: 8px; background: #0d344b; border: 1px solid #31576a; border-top: 2px solid #00b8e3;
          border-radius: 0 0 7px 7px; box-shadow: 0 18px 38px rgba(1,15,23,0.42);
        }
        .topbar-group:nth-last-child(-n+2) .topbar-group-menu { left: auto; right: 0; }
        .topbar-group-menu-title { display: flex; align-items: center; gap: 8px; padding: 5px 6px 10px; border-bottom: 1px solid #31576a; margin-bottom: 5px; }
        .topbar-group-menu-title span { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; background: rgba(0,184,227,0.13); color: #00b8e3; font-size: 10px; font-weight: 900; }
        .topbar-group-menu-title strong { color: #edf8ff; font-size: 12px; }
        .topbar-group-menu-items, .module-group-items { display: grid; gap: 3px; }
        .topbar-group-menu-items button, .module-group-items button {
          width: 100%; min-height: 47px; display: flex; align-items: center; justify-content: space-between; gap: 8px;
          padding: 7px 8px; border: 1px solid transparent; border-radius: 5px; background: transparent;
          text-align: left; cursor: pointer; font-family: 'Space Grotesk', sans-serif;
        }
        .topbar-group-menu-items button:hover, .topbar-group-menu-items button.is-active,
        .module-group-items button:hover, .module-group-items button.is-active { background: rgba(0,184,227,0.09); border-color: rgba(0,184,227,0.27); }
        .topbar-group-menu-items button span, .module-group-items button span { min-width: 0; }
        .topbar-group-menu-items button strong, .module-group-items button strong { display: block; color: #edf8ff; font-size: 11px; font-weight: 700; }
        .topbar-group-menu-items button small, .module-group-items button small { display: block; margin-top: 2px; color: #8ca7b6; font-size: 9px; line-height: 1.25; }
        .topbar-group-menu-items button > b, .module-group-items button > b { color: #00b8e3; font-size: 17px; font-weight: 500; }
        .topbar-group-menu-items button em, .module-group-items button em { padding: 2px 5px; border-radius: 8px; background: rgba(159,180,193,0.11); color: #8ca7b6; font-size: 8px; font-style: normal; }
        .topbar-group-menu-items button:disabled, .module-group-items button:disabled { opacity: 0.58; cursor: default; }
        .module-launcher, .topbar-panel-button, .topbar-logout {
          min-height: 34px; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
          border: 1px solid rgba(159,180,193,0.28); border-radius: 5px; background: rgba(255,255,255,0.035);
          color: #d9eaf2; padding: 0 12px; font: 700 11.5px 'Space Grotesk', sans-serif; letter-spacing: 0.03em;
          cursor: pointer; white-space: nowrap;
        }
        .module-launcher:hover, .module-launcher.is-open, .topbar-panel-button:hover, .topbar-logout:hover { color: #00b8e3; border-color: rgba(0,184,227,0.55); background: rgba(0,184,227,0.08); }
        .module-launcher-grid { width: 14px; height: 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px; }
        .module-launcher-grid i { display: block; border: 1px solid currentColor; border-radius: 1px; }
        .mobile-module-launcher { display: none; }
        .app-topbar-actions { display: flex; align-items: center; justify-content: flex-end; gap: 9px; flex: 0 0 auto; }
        .topbar-panel-button { color: #00b8e3; border-color: rgba(0,184,227,0.3); }
        .panel-label-short { display: none; }
        .topbar-user { max-width: 120px; overflow: hidden; text-overflow: ellipsis; color: #9fb4c1; font-size: 11px; white-space: nowrap; }
        .topbar-logout { color: #9fb4c1; text-transform: uppercase; }
        .module-menu-shell {
          position: absolute; top: calc(100% + 1px); left: 8px; right: 8px; max-width: 760px; max-height: calc(100vh - 68px);
          margin: 0 auto; overflow-y: auto; background: #0d344b; border: 1px solid #31576a; border-top: 2px solid #00b8e3;
          border-radius: 0 0 8px 8px; box-shadow: 0 22px 50px rgba(1,15,23,0.42);
        }
        .module-menu-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 14px 16px 12px; border-bottom: 1px solid #31576a; }
        .module-menu-heading strong { display: block; color: #edf8ff; font-size: 14px; }
        .module-menu-heading span { display: block; margin-top: 2px; color: #9fb4c1; font-size: 11px; }
        .module-menu-heading button { width: 30px; height: 30px; border: 1px solid #31576a; border-radius: 5px; background: transparent; color: #9fb4c1; cursor: pointer; }
        .module-menu-heading button:hover { color: #00b8e3; border-color: #00b8e3; }
        .module-menu-groups { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; padding: 10px; }
        .module-group { min-width: 0; padding: 10px; background: #0b2e43; border: 1px solid #294f63; border-radius: 6px; }
        .module-group-title { min-height: 36px; display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .module-group-title > span { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; border-radius: 5px; background: rgba(0,184,227,0.13); color: #00b8e3; font-size: 10px; font-weight: 900; letter-spacing: 0.04em; }
        .module-group-title strong { color: #dcebf2; font-size: 11px; line-height: 1.25; }
        @media (max-width: 1450px) {
          .topbar-group-trigger { padding-left: 9px; padding-right: 9px; font-size: 10.5px; }
          .topbar-user { display: none; }
        }
        @media (max-width: 1180px) {
          .topbar-groups { display: none; }
          .mobile-module-launcher { display: inline-flex; }
          .app-topbar-actions { margin-left: auto; }
        }
        @media (max-width: 700px) {
          .app-topbar-inner { height: 54px; padding: 0 10px; gap: 8px; }
          .app-brand-name, .theme-toggle--nav .theme-toggle__label, .panel-label-long { display: none; }
          .panel-label-short { display: inline; }
          .module-launcher { padding: 0 8px; }
          .topbar-panel-button, .topbar-logout { padding: 0 8px; }
          .app-topbar-actions { gap: 5px; }
        }
        @media (max-width: 500px) {
          .module-menu-groups { grid-template-columns: 1fr; }
          .app-brand-mark { display: none; }
          .app-brand strong { letter-spacing: 2px; }
          .module-launcher { gap: 5px; }
          .module-launcher-grid { width: 12px; height: 12px; }
        }
      `}</style>
    </div>
    </ErrorBoundary>
  );
}
